// javascript
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import GavelIcon from '@mui/icons-material/Gavel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import BotonWhatsapp from '../components/BotonWhatsapp';
import cambioEstadoService from '../services/cambioEstadoService';
import { obtenerArchivosPorTramite, descargarArchivo } from '../services/archivoService';

function GestorEstadoSentenciaJudicial({ tramite, onEstadoCambiado }) {
    const [cargando, setCargando] = useState(false);
    const [cargandoHistorial, setCargandoHistorial] = useState(true);
    const [infoSentencia, setInfoSentencia] = useState(null);
    const [infoJudicial, setInfoJudicial] = useState({ juzgado: null, abogado: null });
    const [archivosDelTramite, setArchivosDelTramite] = useState([]);
    const [archivosSentencia, setArchivosSentencia] = useState([]);

    // --- NUEVO ESTADO PARA EL MODAL DE CONFIRMACIÓN ---
    const [mostrarModalCierre, setMostrarModalCierre] = useState(false);

    useEffect(() => {
        cargarDatos();
    }, [tramite.idTramite]);

    const cargarDatos = async () => {
        setCargandoHistorial(true);
        try {
            // Cargar historial y archivos en paralelo
            const [historial, archivos] = await Promise.all([
                cambioEstadoService.getHistorialEstados(tramite.idTramite),
                obtenerArchivosPorTramite(tramite.idTramite)
            ]);

            const archivosMapeados = archivos.map(a => ({
                idArchivo: a.idArchivo,
                nombre: a.nombre,
                tamaño: (a.tamaño / (1024 * 1024)).toFixed(2) + " MB"
            }));
            setArchivosDelTramite(archivosMapeados);

            // 1. Buscar info de la sentencia (Estado 18 -> 12)
            const cambioSentencia = historial
                .filter(c => c.comentario && c.comentario.includes('Sentencia Judicial Registrada'))
                .pop();

            let nombresArchivosSentencia = [];
            if (cambioSentencia && cambioSentencia.comentario) {
                nombresArchivosSentencia = parsearInfoSentencia(cambioSentencia.comentario);
            }

            // 2. Buscar info del juzgado (Estado 10 -> 11)
            const cambioDemanda = historial
                .filter(c => c.comentario && c.comentario.includes('Segunda Apelación (Demanda Judicial) registrada'))
                .pop();

            let juzgado = null;
            if (cambioDemanda && cambioDemanda.comentario) {
                const juzgadoMatch = cambioDemanda.comentario.match(/Juzgado: (.+?)(?:\n|$)/);
                if (juzgadoMatch) juzgado = juzgadoMatch[1].trim();
            }

            // 3. Buscar info del abogado (Estado 11 -> 16)
            const cambioPresentacion = historial
                .filter(c => c.comentario && c.comentario.includes('Presentación de documentación registrada'))
                .pop();

            let abogado = null;
            if (cambioPresentacion && cambioPresentacion.comentario) {
                const abogadoMatch = cambioPresentacion.comentario.match(/Abogado responsable: (.+?)(?:\n|$)/);
                if (abogadoMatch && !abogadoMatch[1].includes('No especificado')) {
                    abogado = abogadoMatch[1].trim();
                }
            }

            setInfoJudicial({ juzgado, abogado });

            // 4. Filtrar los archivos de la sentencia
            if (nombresArchivosSentencia.length > 0) {
                const archivosFiltrados = archivosMapeados.filter(a =>
                    nombresArchivosSentencia.includes(a.nombre)
                );
                setArchivosSentencia(archivosFiltrados);
            }

        } catch (error) {
            console.error("Error al cargar datos:", error);
            toast.error("No se pudo cargar la información de la sentencia.");
        } finally {
            setCargandoHistorial(false);
        }
    };

    const parsearInfoSentencia = (comentario) => {
        const info = {
            resultado: null,
            fecha: null,
            observaciones: null,
            archivosAdjuntos: []
        };

        const resultadoMatch = comentario.match(/Resultado: (.+?)(?:\n|$)/);
        if (resultadoMatch) info.resultado = resultadoMatch[1].trim();

        const fechaMatch = comentario.match(/Fecha de Sentencia: (.+?)(?:\n|$)/);
        if (fechaMatch) info.fecha = fechaMatch[1].trim();

        const obsMatch = comentario.match(/Observaciones: (.+?)(?:\n|$)/);
        if (obsMatch && !obsMatch[1].includes('Sin observaciones')) info.observaciones = obsMatch[1].trim();

        const archivosMatch = comentario.match(/Archivos adjuntos: (.+?)(?:\n|$)/);
        if (archivosMatch) {
            info.archivosAdjuntos = archivosMatch[1].split(',').map(nombre => nombre.trim());
        }

        setInfoSentencia(info);
        return info.archivosAdjuntos; // Devuelve los nombres para filtrar
    };

    const handleDescargarArchivo = async (idArchivo, nombre) => {
        try {
            const response = await descargarArchivo(idArchivo);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nombre);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error("Error al descargar el archivo.");
        }
    };

// Lógica de cierre de trámite...
    const ejecutarCierreTramite = async () => {
        if (!infoSentencia || !infoSentencia.resultado) {
            toast.error("No se puede cerrar el trámite, no se encontró el resultado de la sentencia.");
            return;
        }

        const esFavorable = infoSentencia.resultado === "Favorable";
        const siguienteEstadoId = esFavorable ? 8 : 13; // 8: Completo, 13: Rechazado
        const estadoNombre = esFavorable ? "Completo" : "Rechazado";
        const comentarioFinal = esFavorable
            ? `Trámite completado con sentencia judicial FAVORABLE.\n\nResultado: Favorable\nFecha de Sentencia: ${infoSentencia.fecha}\nJuzgado: ${infoJudicial.juzgado || 'No especificado'}\n\nEl tribunal emitió una sentencia favorable al cliente. El trámite ha sido completado exitosamente.`
            : `Trámite rechazado con sentencia judicial DESFAVORABLE.\n\nResultado: Desfavorable\nFecha de Sentencia: ${infoSentencia.fecha}\nJuzgado: ${infoJudicial.juzgado || 'No especificado'}\n\nEl tribunal emitió una sentencia desfavorable. El trámite ha sido rechazado.`;

        setCargando(true);
        try {
            const cambioEstadoData = {
                idEstado: siguienteEstadoId,
                comentario: comentarioFinal
            };

            await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);

            toast.success(`Trámite cerrado y movido al estado "${estadoNombre}".`);

            setMostrarModalCierre(false); // <-- Cerrar el modal

            if (onEstadoCambiado) {
                await onEstadoCambiado();
            }

        } catch (error) {
            console.error("Error al cerrar trámite:", error);
            toast.error("Error al cerrar el trámite: " + (error.response?.data || error.message));
        } finally {
            setCargando(false);
        }
    };

    // --- NUEVA FUNCIÓN PARA RENDERIZAR EL MODAL DE CONFIRMACIÓN ---
    const renderModalConfirmacionCierre = () => {
        if (!infoSentencia) return null;

        const esFavorable = infoSentencia.resultado === "Favorable";
        const estadoNombre = esFavorable ? "Completo" : "Rechazado";
        const colorBoton = esFavorable ? "btn-success" : "btn-danger";
        const borderColor = esFavorable ? "border-success" : "border-danger";
        const textColor = esFavorable ? "text-success" : "text-danger";
        const Icono = esFavorable ? CheckCircleIcon : WarningIcon;

        return (
            <div className="mt-4">
                <div className={`card shadow-sm border-2 rounded-4 p-4 text-center ${borderColor}`}>
                    <h5 className={`fw-semibold ${textColor} mb-3`}>
                        <Icono className="me-2" />
                        Confirmar Cierre de Trámite
                    </h5>
                    <p className="text-muted">
                        ¿Confirma que desea cerrar el trámite? El resultado de la sentencia es <strong>{infoSentencia.resultado}</strong>.
                    </p>
                    <p className="text-muted">
                        El estado final del trámite será: <strong>"{estadoNombre}"</strong>.
                    </p>
                    <div className="d-flex justify-content-center gap-3 mt-3">
                        <button
                            className={`btn ${colorBoton} d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold`}
                            onClick={ejecutarCierreTramite}
                            disabled={cargando}
                        >
                            <Icono fontSize="small" />
                            {cargando ? 'Cerrando...' : `Sí, Cerrar como ${estadoNombre}`}
                        </button>
                        <button
                            className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                            onClick={() => setMostrarModalCierre(false)}
                            disabled={cargando}
                        >
                            <CancelIcon fontSize="small" />
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        );
    };


    if (cargandoHistorial) {
        return (
            <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
                <div className="alert alert-info">Cargando información de la sentencia...</div>
            </div>
        );
    }

    if (!infoSentencia) {
        return (
            <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
                <div className="alert alert-warning">No se pudo encontrar el registro de la sentencia en el historial.</div>
            </div>
        );
    }

    const esFavorable = infoSentencia.resultado === "Favorable";

    const mensajeNotificacion = `Hola {nombre}, le informamos que ha salido la Sentencia Judicial con resultado: ${infoSentencia?.resultado?.toUpperCase()}. Por favor contacte con el estudio para conocer los pasos a seguir. Saludos.`;

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className={`fw-bold ${esFavorable ? 'text-success' : 'text-danger'} mb-0`}>
                    <GavelIcon className="me-2" />
                    Sentencia Judicial Publicada
                </h4>
            </div>

            <div className={`alert ${esFavorable ? 'alert-success' : 'alert-danger'} border-0 rounded-3`}>
                <strong>Resultado: {infoSentencia.resultado}</strong>
                <p className="mb-0 mt-1">
                    {esFavorable
                        ? "El tribunal emitió una sentencia favorable. El trámite está listo para ser completado."
                        : "El tribunal emitió una sentencia desfavorable. El trámite está listo para ser cerrado como rechazado."}
                </p>
            </div>

            {/* Resumen de la Sentencia */}
            <div className="mt-3 p-3 bg-light rounded-3 border mb-4">
                <h6 className="fw-semibold text-secondary mb-3">Resumen de la Sentencia </h6>
                <div className="row g-3">
                    <div className="col-md-6 d-flex align-items-center">
                        {esFavorable ? (
                            <CheckCircleIcon className="text-success me-2" />
                        ) : (
                            <CancelIcon className="text-danger me-2" />
                        )}
                        <div>
                            <small className="text-muted d-block">Resultado</small>
                            <strong className={`text-dark ${esFavorable ? 'text-success' : 'text-danger'}`}>{infoSentencia.resultado}</strong>
                        </div>
                    </div>

                    <div className="col-md-6 d-flex align-items-center">
                        <CalendarMonthIcon className="text-primary me-2" />
                        <div>
                            <small className="text-muted d-block">Fecha de Sentencia</small>
                            <strong className="text-dark">{infoSentencia.fecha}</strong>
                        </div>
                    </div>

                    {infoJudicial.juzgado && (
                        <div className="col-md-6 d-flex align-items-center">
                            <BusinessIcon className="text-primary me-2" />
                            <div>
                                <small className="text-muted d-block">Juzgado</small>
                                <strong className="text-dark">{infoJudicial.juzgado}</strong>
                            </div>
                        </div>
                    )}

                    {infoJudicial.abogado && (
                        <div className="col-md-6 d-flex align-items-center">
                            <PersonIcon className="text-primary me-2" />
                            <div>
                                <small className="text-muted d-block">Abogado Responsable</small>
                                <strong className="text-dark">{infoJudicial.abogado}</strong>
                            </div>
                        </div>
                    )}

                    {infoSentencia.observaciones && (
                        <div className="col-12">
                            <small className="text-muted d-block">Observaciones</small>
                            <p className="text-dark mb-0">{infoSentencia.observaciones}</p>
                        </div>
                    )}

                    {/* Archivos Adjuntos */}
                    {archivosSentencia.length > 0 && (
                        <div className="col-12 mt-3">
                            <h6 className="fw-semibold text-primary mb-2">Archivos de Sentencia</h6>
                            <div className="list-group">
                                {archivosSentencia.map(archivo => (
                                    <div key={archivo.idArchivo} className="list-group-item d-flex justify-content-between align-items-center">
                                        <div>
                                            <AttachFileIcon fontSize="small" className="me-2 text-muted" />
                                            <span className="fw-semibold">{archivo.nombre}</span>
                                            <small className="text-muted ms-2">({archivo.tamaño})</small>
                                        </div>
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => handleDescargarArchivo(archivo.idArchivo, archivo.nombre)}
                                            title="Descargar"
                                        >
                                            <DownloadIcon fontSize="small" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div>
                {/* --- SECCIÓN DE ACCIONES MODIFICADA --- */}
                {mostrarModalCierre ? (
                    // Si mostrarModalCierre es true, renderiza el modal
                    renderModalConfirmacionCierre()
                ) : (
                    // Si no, renderiza los botones de acción
                    <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                        <BotonWhatsapp
                            idEvento={infoSentencia?.idEvento}
                            numeroTelefono={tramite.cliente?.telefonoPropio || tramite.cliente?.telefono}
                            nombreCliente={`${tramite.cliente?.nombre || ''} ${tramite.cliente?.apellido || ''}`.trim()}
                            contactosAdicionales={tramite.cliente?.contactos}
                            textoBoton="Enviar Notificación"
                            tramiteId={tramite?.idTramite}
                            className="btn-sm"
                        />
                        <button
                            className={`btn ${esFavorable ? 'btn-success' : 'btn-danger'} d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold`}
                            onClick={() => setMostrarModalCierre(true)}
                            disabled={cargando}
                        >
                            {esFavorable ? <CheckCircleIcon fontSize="small" /> : <WarningIcon fontSize="small" />}
                            Cerrar Trámite
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
export default GestorEstadoSentenciaJudicial;

